import { useTranslation } from 'react-i18next'
import { useCallback, useEffect, useRef, useState } from 'react'
import { apiClient } from '@/lib/apiClient'

interface TenantDocument {
  id: string
  filename: string
  contentType: string
  sizeBytes: number
  kind: string
  ocrProcessedAt: string | null
  createdAt: string
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function DocumentsPage() {
  const { t } = useTranslation()
  const fileRef = useRef<HTMLInputElement>(null)
  const [docs, setDocs] = useState<TenantDocument[]>([])
  const [loadError, setLoadError] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [toast, setToast] = useState<string | null>(null)

  function flash(msg: string) {
    setToast(msg)
    setTimeout(() => setToast(null), 3000)
  }

  const load = useCallback(async () => {
    try {
      const res = await apiClient.get<TenantDocument[]>('/tenant-documents')
      setDocs(res.data)
    } catch {
      setLoadError(true)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    try {
      const fd = new FormData()
      fd.append('file', file)
      await apiClient.post('/tenant-documents', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      void load()
    } catch {
      flash(t('documents.error_upload'))
    } finally {
      setUploading(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  if (loadError) {
    return <p className="text-red-600 p-6">{t('documents.error_load')}</p>
  }

  return (
    <div className="space-y-6 p-6">
      {toast && (
        <div className="fixed right-6 top-20 z-50 rounded-lg border bg-card px-4 py-2 text-sm shadow-lg">
          {toast}
        </div>
      )}

      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{t('documents.title')}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t('documents.subtitle')}</p>
        </div>
        <div>
          <input
            ref={fileRef}
            type="file"
            className="hidden"
            onChange={e => void handleFileChange(e)}
          />
          <button
            className="bg-blue-600 text-white text-sm px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
            disabled={uploading}
            onClick={() => fileRef.current?.click()}
          >
            {uploading ? t('documents.uploading') : t('documents.upload')}
          </button>
        </div>
      </div>

      {docs.length === 0 ? (
        <p className="text-muted-foreground">{t('documents.empty')}</p>
      ) : (
        <div className="border rounded-lg overflow-hidden bg-card shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-card border-b">
              <tr>
                <th className="text-left px-4 py-2 font-medium text-foreground">
                  {t('documents.col_filename')}
                </th>
                <th className="text-left px-4 py-2 font-medium text-foreground">
                  {t('documents.col_kind')}
                </th>
                <th className="text-left px-4 py-2 font-medium text-foreground">
                  {t('documents.col_size')}
                </th>
                <th className="text-left px-4 py-2 font-medium text-foreground">
                  {t('documents.col_ocr')}
                </th>
                <th className="text-left px-4 py-2 font-medium text-foreground">
                  {t('documents.col_uploaded')}
                </th>
              </tr>
            </thead>
            <tbody>
              {docs.map(doc => (
                <tr key={doc.id} className="border-b last:border-0">
                  <td className="px-4 py-2 font-medium">{doc.filename}</td>
                  <td className="px-4 py-2 text-muted-foreground">
                    {t(`documents.kind_${doc.kind}` as Parameters<typeof t>[0], {
                      defaultValue: doc.kind,
                    })}
                  </td>
                  <td className="px-4 py-2 text-muted-foreground">{formatBytes(doc.sizeBytes)}</td>
                  <td className="px-4 py-2">
                    {doc.ocrProcessedAt ? (
                      <span className="text-xs bg-green-900/40 text-green-300 px-2 py-0.5 rounded-full">
                        {t('documents.ocr_done')}
                      </span>
                    ) : (
                      <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full">
                        {t('documents.ocr_pending')}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-xs text-muted-foreground">
                    {new Date(doc.createdAt).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
